import $ from 'jquery';
import debounce from 'lodash.debounce';
import * as loader from './loader';
import * as suggest from './suggest';
import * as pagination from './pagination';
import * as scrollTo from './scrollTo';
import { buildUrl, priceListQueryStringKey, setSearchParam, getQueryText } from './search';
import { MQ } from '../tools/MQ.js';

export const init = ($target) => {
	const $priceFilter = $('.js-price-filter'),
		$searchBox = $priceFilter.find('.js-search-box'),
		$priceList = $('#js-price-list');

	if (!$priceFilter.length || !$searchBox.length || !$priceList.length) {
		return;
	}

	///////////////////////////////////////////////////////////////////////////////
	/// CONSTANTS AND VARIABLES
	///////////////////////////////////////////////////////////////////////////////

	const DEBOUNCE_WAIT_MS = 500,
		API_BASE_URL = $searchBox.attr('action'),
		SUGGEST_BASE_URL = $searchBox.data('suggest-base-url'),
		DROPDOWN_DEFAULT_CUSTOM_VALUE = 'ALL';

	const $regionDropdown = $priceFilter.find('#region-dropdown'),
		$districtDropdown = $priceFilter.find('#district-dropdown'),
		$yearDropdown = $priceFilter.find('#year-dropdown'),
		$queryTextInput = $priceFilter.find('input[name=queryText]'),
		$loader = $('.js-loader');

	///////////////////////////////////////////////////////////////////////////////
	/// INITIALIZATION
	///////////////////////////////////////////////////////////////////////////////

	$target.ready(async function () {
		await initFilterValues(new URLSearchParams(window.location.search));

		await attachCallbacks();

		onSubmit(true);
	});

	async function initFilterValues(urlSearchParams) {
		const priceListType = urlSearchParams.get(priceListQueryStringKey.type),
			queryText = urlSearchParams.get(priceListQueryStringKey.queryText),
			commodity = urlSearchParams.get(priceListQueryStringKey.commodity),
			region = urlSearchParams.get(priceListQueryStringKey.region),
			district = urlSearchParams.get(priceListQueryStringKey.district),
			year = urlSearchParams.get(priceListQueryStringKey.year);

		// text input
		$queryTextInput.val(queryText?.trim());

		// price list type radios
		if (priceListType) selectRadioButton(priceListQueryStringKey.type, priceListType);

		// commodity radios
		if (commodity) selectRadioButton(priceListQueryStringKey.commodity, commodity);

		// region dropdown
		selectDropdownItem($regionDropdown, region);

		// district dropdown
		const visibleDistricts = await updateDropdownDistricts(region);
		if (district) {
			selectDropdownItem($districtDropdown, district);
		} else if (visibleDistricts.length === 1) {
			selectDropdownItem($districtDropdown, visibleDistricts[1]);
		} else {
			selectDropdownItem($districtDropdown, DROPDOWN_DEFAULT_CUSTOM_VALUE);
		}

		// year dropdown
		selectDropdownItem($yearDropdown, year);
	}

	async function attachCallbacks() {
		attachOnPopStateCallback();
		attachSearchBoxCallbacks();
		attachFilterCallbacks();
		initPagination();
	}

	async function attachFilterCallbacks() {
		attachRadiosCallbacks();
		attachDropdownCallbacks($districtDropdown);
		attachDropdownCallbacks($yearDropdown);
		attachRegionDropdownCallback();
	}

	async function detachFilterCallbacks() {
		detachRadiosCallbacks();
		detachDropdownCallbacks($districtDropdown);
		detachDropdownCallbacks($yearDropdown);
		detachDropdownCallbacks($regionDropdown);
	}

	/**
	 * Previous page callback.
	 */
	function attachOnPopStateCallback() {
		window.onpopstate = async function (event) {
			await detachFilterCallbacks();

			await initFilterValues(new URLSearchParams(event.target.location.search));

			onSubmit(true);

			attachFilterCallbacks();
		};
	}

	function attachSearchBoxCallbacks() {
		$queryTextInput.on('input', debounce(onInput, DEBOUNCE_WAIT_MS));

		$searchBox.on('submit', function (event) {
			event.preventDefault();

			onSubmit(false, 1);
		});
	}

	function initPagination() {
		pagination.init('#js-price-list', onPaginationChange);

		function onPaginationChange(page) {
			onSubmit(false, page);
		}
	}

	function attachRadiosCallbacks() {
		$priceFilter.find('input[type=radio]').on('change', () => onSubmit(false, 1));
	}

	function detachRadiosCallbacks() {
		$priceFilter.find('input[type=radio]').off('change');
	}

	function attachDropdownCallbacks($dropdown) {
		$dropdown.find('input[type=hidden]').on('change', () => onSubmit(false, 1));
	}

	function detachDropdownCallbacks($dropdown) {
		$dropdown.find('input[type=hidden]').off('change');
	}

	function attachRegionDropdownCallback() {
		$regionDropdown.find('input[type=hidden]').on('change', async function (event) {
			const selectedRegion = event.target.value,
				visibleDistricts = await updateDropdownDistricts(selectedRegion);

			let districtToSelect = DROPDOWN_DEFAULT_CUSTOM_VALUE;

			if (visibleDistricts.length === 1) {
				districtToSelect = visibleDistricts[0];
			}

			selectDropdownItem($districtDropdown, districtToSelect);
		});
	}

	///////////////////////////////////////////////////////////////////////////////
	/// SEARCH EVENTS
	///////////////////////////////////////////////////////////////////////////////

	function onInput() {
		const params = getParams();

		let url = `${SUGGEST_BASE_URL}/${params.segment}/${params.priceListType}`;

		// remove segment and type from query string
		params.segment = '';
		params.priceListType = '';
		params.page = '';

		const urlSearchParams = new URLSearchParams();
		setSearchParams(urlSearchParams, params);

		url = buildUrl(url, urlSearchParams);

		suggest.onInput($queryTextInput, url);
	}

	async function onSubmit(queryUpdateReplaceState = false, page = null) {
		const params = getParams($priceFilter);

		// Make price list collapsible if there is no fulltext search
		const isCollapsible = !params.queryText;

		if (page) params.page = page;

		// remove focus from text input
		$queryTextInput.trigger('blur');

		updateQueryString(params, queryUpdateReplaceState);

		loader.show($loader);

		let response = '';
		try {
			response = await search(params);
		} catch (err) {
			if (err.status !== '404') console.error('There seems to be an error with the API');
		}

		// try resubmitting with page 1
		if (response.totalCount > 0 && response.pageCount > 0 && !response.results.length) {
			onSubmit(true, 1);
			return;
		}

		fillSearchResults(response, isCollapsible);
		loader.hide($loader);
		pagination.update(response.pageIndex, response.pageCount);

		// scroll to results (mobile only)
		if (MQ('mdDown')) scrollTo.scrollToId('#js-price-list');

		try {
			pushDataLayer(params, response);
		} catch (err) {
			console.error(err);
		}
	}

	function search(params) {
		let url = `${API_BASE_URL}/${params.searchType}/${params.segment}/${params.priceListType}`;
		// remove search type, segment and type from query string
		params.searchType = '';
		params.segment = '';
		params.priceListType = '';

		const urlSearchParams = new URLSearchParams(window.location.search);
		setSearchParams(urlSearchParams, params);

		url = buildUrl(url, urlSearchParams);

		return $.ajax({
			type: 'GET',
			url: url,
			contentType: 'text/plain',
		});
	}

	function pushDataLayer(params, response) {
		const radio2name = (radios) => radios.get().map((radio) => $(radio).next().text());

		if (!response || !response.results) {
			return;
		}

		const checkedTypesRadios = $priceFilter.find('.price-filter__top input[type=radio]:checked');
		const checkedCommodityRadios = $priceFilter.find('.price-filter__bottom-inner input[type=radio]:checked');
		const selectedRegionName = getDropdownValue($priceFilter.find('#region-dropdown'));
		const selectedDistrictName = getDropdownValue($priceFilter.find('#district-dropdown'));
		const resultCount = response.results.reduce((total, current) => {
			if (current.items) {
				return total + current.items.length;
			}
			if (current.resultsCount) {
				return total + current.resultsCount;
			}
			return total;
		}, 0);

		const dataLayerObject = {
			event: 'internalSearchResult',
			internalSearchType: $searchBox.attr('id'),
			internalSearchKeyword: params.queryText,
			internalSearchResultCount: resultCount,
			internalSearchFilters: {
				commodity: [radio2name(checkedCommodityRadios)],
				types: [radio2name(checkedTypesRadios)],
				regions: [selectedRegionName],
				districts: [selectedDistrictName],
			},
		};

		dataLayer.push(dataLayerObject);
	}

	///////////////////////////////////////////////////////////////////////////////
	/// UTILS
	///////////////////////////////////////////////////////////////////////////////

	/**
	 * Updates districts' visiblity based on the selected region.
	 * @param {string} region Selected region
	 * @returns visible districts' code names
	 */
	async function updateDropdownDistricts(region) {
		$districtDropdown.find('.js-dropdown-item').each(function () {
			const districtRegion = $(this).data('group-code');

			if (districtRegion === region) {
				$(this).show();
			} else {
				$(this).hide();
			}
		});

		const visibleDistricts = $districtDropdown.find('.js-dropdown-item:visible'),
			dropdownBtn = $districtDropdown.find('.js-dropdown-toggle');

		if (visibleDistricts.length === 0) {
			dropdownBtn.addClass('is-disabled');
			dropdownBtn.attr('tabindex', '-1');
		} else {
			dropdownBtn.removeClass('is-disabled');
			dropdownBtn.removeAttr('tabindex');
		}

		return visibleDistricts.map((_, dropdownItem) => $(dropdownItem).data('custom-value'));
	}

	/**
	 * Updates url query string
	 * @param {object} params
	 * @param {boolean} replaceState
	 * @returns true if the url has been updated, otherwise false
	 */
	function updateQueryString(params, replaceState = false) {
		const pageUrlSearchParams = new URLSearchParams(window.location.search);
		const oldQueryString = pageUrlSearchParams.toString();

		setSearchParams(pageUrlSearchParams, params);

		const newQueryString = pageUrlSearchParams.toString();

		if (oldQueryString === newQueryString) {
			return false;
		}

		if (replaceState) {
			window.history.replaceState(null, '', '?' + newQueryString);
		} else {
			window.history.pushState(null, '', '?' + newQueryString);
		}

		return true;
	}

	function selectDropdownItem($dropdown, customValue) {
		if (!customValue) customValue = DROPDOWN_DEFAULT_CUSTOM_VALUE;

		let dropdownItem = $dropdown
			.find('.js-dropdown-item')
			.filter(function () {
				return $(this).data('custom-value') == customValue;
			})
			.first();

		const $dropdownToggle = $dropdown.find('.js-dropdown-toggle'),
			$itemLink = dropdownItem.find('.js-dropdown-link');

		$dropdownToggle.trigger('click');
		$itemLink.trigger('click');
	}

	function selectRadioButton(name, value) {
		$(`input[name=${name}]`).prop('checked', false);
		$(`input[name=${name}][value=${value}]`).prop('checked', true);
	}

	function fillSearchResults(response, isCollapsible) {
		let priceListHtml = '';

		if (!response || !response.results || !response.results.length) {
			priceListHtml = getNoResultsItemHtml();
			pagination.hide();
		} else {
			priceListHtml = getPriceListHtml(response.results, isCollapsible);
			pagination.show();
		}

		$priceList.html(priceListHtml);
		attachSearchResultsCallbacks();
		attachPriceListEventListeners();
	}

	function attachSearchResultsCallbacks() {
		$priceList.find('.price-list__link').on('click auxclick', function (e) {
			if (e.which === 1 || e.which === 2) {
				let data = {
					event: 'fileDownload',
					fileName: $.trim($(this).text()),
				};
				dataLayer.push(data);
			}
		});
	}

	function attachPriceListEventListeners() {
		const collapsiblePriceItems = $priceList.find('.js-price-item-collapsible');
		collapsiblePriceItems.each((_, collapsiblePriceItem) => {
			$(collapsiblePriceItem).on('click', () => handleCollapseButtonClick(collapsiblePriceItems, $(collapsiblePriceItem)));
		});
	}

	function handleCollapseButtonClick(collapsiblePriceItems, collapsiblePriceItem) {
		function closeOpenProducts() {
			const openedPriceItems = $(collapsiblePriceItems).siblings('.price-list-collapsible__item--active');
			openedPriceItems.each((_, openedPriceItem) => {
				openedPriceItem.classList.toggle('price-list-collapsible__item--active');
				$(openedPriceItem).attr('aria-expanded', 'false');
				$(openedPriceItem).find('.price-list__inners').slideToggle();
			});
		}

		function toggleOpenProduct() {
			collapsiblePriceItem.toggleClass('price-list-collapsible__item--active');
			collapsiblePriceItem.attr('aria-expanded', collapsiblePriceItem.attr('aria-expanded') === 'false' ? 'true' : 'false');
			collapsiblePriceItem.find('.price-list__inners').slideToggle();
		}

		const product = collapsiblePriceItem.data('product');
		const innerElement = collapsiblePriceItem.find('.price-list__inners');

		if (collapsiblePriceItem.attr('aria-expanded') === 'true') {
			toggleOpenProduct();
		} else {
			const productItems = getProductItems(product);
			productItems.then((result) => {
				insertItems(innerElement, result);
				closeOpenProducts();
				toggleOpenProduct();
				scrollTo.scrollToId('#js-price-list');
			});
		}
	}

	async function getProductItems(product) {
		const params = getParams($priceFilter);
		params.searchType = 'getproductpricelists';
		params.product = product;

		loader.show($loader);

		let response = '';
		try {
			response = await search(params);
		} catch (err) {
			if (err.status !== '404') console.error('There seems to be an error with the API');
		}

		loader.hide($loader);

		return response;
	}

	function insertItems(element, result) {
		const items = result?.results[0]?.items;
		$(element).html(items ? items.map((item) => getPriceListInnerItemHtml(item)).join('') : getNoResultsItemHtml());
	}

	function getParams() {
		const queryText = getQueryText($queryTextInput);
		return {
			segment: $priceFilter.find('input[name=segment]').val(),
			priceListType: $priceFilter.find('input[name=type]:checked').val(),
			queryText,
			commodity: $priceFilter.find('input[name=commodity]:checked').val(),
			region: getDropdownValue($priceFilter.find('#region-dropdown')),
			district: getDropdownValue($priceFilter.find('#district-dropdown')),
			year: getDropdownValue($priceFilter.find('#year-dropdown')),
			page: new URLSearchParams(window.location.search).get('page') ?? 1,
			searchType: queryText ? 'fulltextsearch' : 'facetsearch',
			product: '',
		};
	}

	function setSearchParams(urlSearchParams, params) {
		setSearchParam(urlSearchParams, priceListQueryStringKey.segment, params.segment);
		setSearchParam(urlSearchParams, priceListQueryStringKey.type, params.priceListType);
		setSearchParam(urlSearchParams, priceListQueryStringKey.queryText, params.queryText);
		setSearchParam(urlSearchParams, priceListQueryStringKey.commodity, params.commodity);
		setSearchParam(urlSearchParams, priceListQueryStringKey.region, params.region);
		setSearchParam(urlSearchParams, priceListQueryStringKey.district, params.district);
		setSearchParam(urlSearchParams, priceListQueryStringKey.year, params.year);
		setSearchParam(urlSearchParams, priceListQueryStringKey.page, params.page);
		setSearchParam(urlSearchParams, priceListQueryStringKey.searchType, params.searchType);
		setSearchParam(urlSearchParams, priceListQueryStringKey.product, params.product);
	}

	function getDropdownValue($dropdown) {
		const selectedItemCustomValue = $dropdown.find('input[type=hidden]').val();
		return selectedItemCustomValue == DROPDOWN_DEFAULT_CUSTOM_VALUE ? '' : selectedItemCustomValue;
	}

	const getPriceListHtml = (results, isCollapsible) =>
		results.map((item, index) => getPriceListItemHtml(item.product, item.items, item.resultsCount, index, isCollapsible)).join('');

	const getPriceListItemHtml = (product, items, resultsCount, index, isCollapsible) => `
		<div class="price-list__item${isCollapsible ? ` price-list-collapsible__item js-price-item-collapsible` : ''}"
		${isCollapsible ? `data-product='${product}' aria-controls="price-list__inners${index}" aria-expanded="false"` : ''}>
			<h3 class="price-list__title">${product} ${
		isCollapsible ? `<span class="price-list__title__count">(${resultsCount})</span><span class="price-list__title__icon"></span>` : ''
	}</h3>
			<div class="price-list__inners" id="price-list__inners${index}" tabindex="-1">
				${isCollapsible ? '' : items.map((item) => getPriceListInnerItemHtml(item)).join('')}
			</div>
		</div>`;

	const getIconHtml = (iconName) => `
		<span class="icon-svg icon-svg--${iconName}" aria-hidden="true">
			<svg class="icon-svg__svg" xmlns:xlink="http://www.w3.org/1999/xlink">
				<use xlink:href="/img/bg/icons-svg.svg#icon-${iconName}" width="100%" height="100%"
					focusable="false"></use>
			</svg>
		</span>`;

	const downloadIcon = getIconHtml('download');
	const pdfIcon = getIconHtml('pdf');
	const documentIcon = getIconHtml('document');

	const getPriceListInfoHtml = (item) => `
		<span class="price-list__info">
			<span class="price-list__icon">
				<span class="item-icon">
					${item.fileType === '.pdf' ? pdfIcon : documentIcon}
					<span class="item-icon__text">${item.fileType.replace('.', '').toUpperCase()}</span>
				</span>
			</span>
			<span class="price-list__type">${item.fileSize > 0 ? `Velikost: ${item.fileSize} kB` : ''}</span>
		</span>`;

	const getPriceListInnerItemHtml = (item) => `
		<div class="price-list__inner">
			<a href="${item.fileLink}" target="_blank" rel="noopener" class="price-list__link">
				<span class="price-list__text">
					<span class="item-icon item-icon--after item-icon--space-between">
						<span class="item-icon__icon">${downloadIcon}</span>
						<span class="item-icon__text">${item.name}</span>
					</span>
				</span>
				${!!item.fileType ? getPriceListInfoHtml(item) : ''}
			</a>
		</div>`;

	const getNoResultsItemHtml = () => {
		const text = $priceList.data('no-results-text');
		return `
			<div class="price-list__item">
				<p class="price-list__no-results">${text}</p>
			</div>`;
	};
};
